import React, { useEffect, useState } from "react";
import { Button, Col, Form, Input, Row, DatePicker, Upload, notification, Switch, } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { Select, Typography } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { employeeService, roleService } from "../../../../services";
import { changeDocumentTitle } from "../../../../utils/title";

const { Title } = Typography;

function UserForm() {
    const { id } = useParams();
    const [form] = Form.useForm();
    const navigate = useNavigate();

    const [submitLoad, setSubmitLoad] = useState(false);
    const [roles, setRoles] = useState([])

    const [password, setPassword] = useState(false)
    const isEditMode = id !== undefined;

    // Form submission handler
    const handleSubmit = async (values) => {
        setSubmitLoad(true);
        try {
            const formatDate = values.joining_date.toString();
            const joining_date = dayjs(formatDate).format("YYYY-MM-DD");
            const updatedEmployee = {
                ...values,
                is_warehouse: values.is_warehouse ? 1 : 0,
                dob: values.dob ? dayjs(values.dob) : null,
                joining_date: values.joining_date ? joining_date : null,
                image: {},
            };

            if (!isEditMode) {
                updatedEmployee.password = values.password;
            }
            if (isEditMode && !updatedEmployee.password) {
                delete updatedEmployee.password;
            }
            let response;

            if (isEditMode) {
                response = await employeeService.editEmployee(id, updatedEmployee);
            } else {
                response = await employeeService.addEmployee(updatedEmployee);
            }

            setSubmitLoad(false);
            if (response.success) {
                if (!isEditMode) {
                    form.resetFields();
                }
                notification.success({
                    message: "Success",
                    description: isEditMode
                        ? "User updated successfully!"
                        : "User added successfully",
                    placement: "topRight",
                });
                navigate(`/staff/user`)
            } else {
                notification.error({
                    message: "Error",
                    description: `Error: ${response.message}`,
                    placement: "topRight",
                });
            }
        } catch (error) {
            console.log("Error during form submission:", error);
        }
    };

    const getRoles = async () => {
        const { success, data } = await roleService.fetch();
        if (success) {
            setRoles(data)
        }
    }

    const initialize = async () => {
        const { success, data } = await employeeService.viewEmployee(id);
        if (success) {
            form.setFieldsValue({
                ...data,
                dob: data.dob ? dayjs(data.dob) : null,
                joining_date: data.joining_date
                    ? dayjs(data.joining_date)
                    : null,
                is_warehouse: data.user.is_warehouse,
            });
        }
    };

    useEffect(() => {
        changeDocumentTitle(isEditMode ? " Edit Employee" : " Add Employee");
        getRoles();
        if (isEditMode) {
            initialize();
        }
    }, [id]);

    return (
        <div
            style={{
                padding: "0 14px",
                border: "1px solid #ccc",
                borderRadius: "8px",
                backgroundColor: "#FFFFFF",
            }}
        >
            <Row
                gutter={26}
                style={{
                    backgroundColor: "#FBFBFB",
                    marginBottom: 15,
                    padding: "10px",
                    borderRadius: "4px",
                    borderBottom: "1px solid #E8E8E9",
                }}
            >
                <Col span={12}>
                    <h1 style={{ fontWeight: "bold" }}>
                        {isEditMode ? "Edit Employee" : "Add Employee"}
                    </h1>
                </Col>
            </Row>

            <Form
                // requiredMark={false}
                layout="vertical"
                form={form}
                onFinish={handleSubmit}
            >
                <Row gutter={24}>
                    <Col span={6}>
                        <Form.Item label="Name" name="name">
                            <Input placeholder="" />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item label="Address" name="address">
                            <Input placeholder="" />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item label="CNIC" name="cnic">
                            <Input placeholder="28-Oct-2024" />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item label="Date of Birth" name="dob">
                            <DatePicker placeholder="Select Date" style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item label=" Date of Joining" name="joining_date">
                            <DatePicker style={{ width: "100%" }} placeholder="Select Date" />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item label="Qualification" name="qualification">
                            <Input placeholder="" />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item label="Salary" name="salary" rules={[{ required: true, message: "Please Input" }]}>
                            <Input placeholder="" />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item label="Designation" name="designation" rules={[{ required: true, message: "Please Input" }]}>
                            <Input placeholder="" />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item label="Email" name="username" rules={[{ type: "email" }, { required: true, message: "Please input password!" }]}>
                            <Input placeholder="" type="email" />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item label="Password" name="password" rules={[{ required: !isEditMode, message: "Please input password!" }]}
                            onChange={() => setPassword(!!form.getFieldValue('password'))}>
                            <Input.Password placeholder="" autoComplete="new-password" />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item
                            label="Confirm Password"
                            name="password_confirmation"
                            dependencies={["password"]}
                            rules={[
                                {
                                    required: password,
                                    message: "Please input confirm password!",
                                },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue("password") === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(
                                            new Error("Passwords do not match!")
                                        );
                                    },
                                })
                            ]}
                        >
                            <Input.Password placeholder="Enter confirm password" />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item label="Profile Image" style={{ width: "100%" }}>
                            <Upload
                                listType="picture"
                                maxCount={1}
                                beforeUpload={() => false}
                                style={{ width: "100%" }}
                            >
                                <Button icon={<UploadOutlined />} style={{ width: "100%" }}>
                                    Choose File
                                </Button>
                            </Upload>
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item
                            name="role"
                            label="Role"
                            rules={[
                                {
                                    required: true,
                                    message: "Select Role"
                                }
                            ]}
                        >
                            <Select placeholder="Select Role" style={{ width: "100%" }}>
                                {roles.map((role) => (
                                    <Select.Option key={role.id} value={role.name}>
                                        {role.title}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item
                            label="Warehouse"
                            name="is_warehouse"
                        >
                            <Switch />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item style={{ textAlign: "center", marginTop: 10 }}>
                            <Button
                                type="primary"
                                style={{ width: "50%" }}
                                htmlType="submit"
                                loading={submitLoad}
                            >
                                {isEditMode ? "Update Employee" : "ADD Employee"}
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </div>
    );
}

export default UserForm;
